import { IOperationResultNoValue } from '@ViewModels';
import { css } from 'aphrodite';
import { makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { withRouter } from 'react-router';
import { RouteComponentProps } from 'react-router-dom';
import { IPlatformError } from '../../../../models/AdminModels';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	IUserSessionComponentProps,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import { AccountUserViewModel, AdminPermission } from '../../../../viewmodels/AdminViewModels';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { Modal } from '../../../../web/components/Modal';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { ConditionalAccess } from '../../ConditionalAccess';
import { styleSheet } from './styles';

interface IProps extends RouteComponentProps<any>, IErrorMessageComponentProps, IUserSessionComponentProps {
	className?: string;
	onClose(): void;
	user?: AccountUserViewModel;
}

interface IState {
	errors?: IPlatformError[];
}

class _UserErrorsDialog extends React.Component<IProps, IState> {
	private mLoadingPromise: Promise<IPlatformError[]>;

	constructor(props: IProps) {
		super(props);

		makeObservable<_UserErrorsDialog, 'mLoadingPromise'>(this, {
			mLoadingPromise: observable.ref,
		});

		this.state = {
			errors: [],
		};
	}

	public componentDidUpdate(prevProps: IProps) {
		if (prevProps?.user !== this.props?.user && this.props?.user) {
			const promise = this.props.user.getErrors();
			promise
				.then(result => {
					this.setState({ errors: result || [] }, () => (this.mLoadingPromise = null));
				})
				.catch((err: IOperationResultNoValue) => {
					const { errorMessages } = this.props;

					errorMessages.pushApiError(err);

					this.mLoadingPromise = null;
				});
			this.mLoadingPromise = promise;
		}
	}

	public render() {
		const { user, className, onClose } = this.props;
		const { errors } = this.state;
		const isOpen = !!user;
		const displayName = user?.name || '';
		const dateFormatString = 'MM-DD-YYYY - hh:mm:ss A';

		return (
			<Modal className={className || ''} isOpen={isOpen} onRequestClose={onClose} useDefaultHeader={true}>
				<div style={{ position: 'relative' }}>
					<div
						className={css(baseStyleSheet.truncateText, styleSheet.title)}
						title={displayName ? displayName : undefined}
					>
						{displayName}
					</div>
					<div className={css(styleSheet.errorSubtitle)}>Errors</div>

					<div className={css(styleSheet.container)}>
						{this.mLoadingPromise ? (
							<div>
								<LoadingSpinner />
							</div>
						) : !errors || errors.length < 1 ? (
							<div className={css(styleSheet.errorsNone)}>No recent errors</div>
						) : (
							<div>
								{errors.map((error, i) => (
									<ConditionalAccess
										elseChildren={
											<div className={css(styleSheet.error)}>
												<span className={css(styleSheet.errorMessage)}>
													{error.message}
													<br />
												</span>
												<span className={css(styleSheet.errorDate)}>
													{moment(error.last_seen).format(dateFormatString)}
												</span>
											</div>
										}
										key={`${error.last_seen || ''}-${i}`}
										permission={AdminPermission.ViewErrorDetail}
									>
										<div className={css(styleSheet.error, styleSheet.errorLink)} onClick={this.openLink(error.appUrl)}>
											<span className={css(styleSheet.errorMessage)}>
												{error.message}
												<br />
											</span>
											<span className={css(styleSheet.errorClass)}>
												{error.error_class}
												<br />
											</span>
											<span className={css(styleSheet.errorContext)}>
												{error.context}
												<br />
											</span>
											<span className={css(styleSheet.errorDate)}>
												{moment(error.last_seen).format(dateFormatString)}
											</span>
										</div>
									</ConditionalAccess>
								))}
							</div>
						)}
					</div>
				</div>
			</Modal>
		);
	}

	private openLink = (url: string) => () => {
		window.open(url, '_blank');
	};
}

const UserErrorsDialogAsObserver = observer(_UserErrorsDialog);
const UserErrorsDialogWithContext = inject(
	ErrorMessagesViewModelKey,
	UserSessionViewModelKey
)(UserErrorsDialogAsObserver);
export const UserErrorsDialog = withRouter(UserErrorsDialogWithContext);
